import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Collapse,
  Divider,
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  Quiz as QuizIcon,
  People as PeopleIcon,
  Menu as MenuIcon,
  School as SchoolIcon,
  Schedule as ScheduleIcon,
  NotificationsActive as NotificationsActiveIcon,
  Email as EmailIcon,
  MarkEmailRead as MarkEmailReadIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material'
// Note: DescriptionIcon, QuizIcon, PeopleIcon are used in drawer menu categories
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

interface NavigationItem {
  label: string
  path: string
  icon: React.ReactNode
  roles?: string[]
}

interface MenuCategory {
  id: string
  label: string
  icon: React.ReactNode
  roles?: string[]
  children: NavigationItem[]
}

// 下部ナビゲーションに表示する主要アイテム（2個 + メニューボタン = 3個）
const getMainNavigationItems = (t: (key: string) => string): NavigationItem[] => [
  {
    label: t('nav.dashboard'),
    path: '/dashboard',
    icon: <DashboardIcon />,
  },
  {
    label: t('nav.myEnrollments'),
    path: '/my-enrollments',
    icon: <AssignmentIcon />,
  },
]

// ドロワーメニュー用の階層構造
const getMenuCategories = (t: (key: string) => string): MenuCategory[] => [
  {
    id: 'learningPlanning',
    label: t('nav.learningPlanning'),
    icon: <SchoolIcon />,
    roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
    children: [
      {
        label: t('nav.learningPlans'),
        path: '/learning-plans',
        icon: <SchoolIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.compliance'),
        path: '/compliance',
        icon: <DescriptionIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.testSchedules'),
        path: '/test-schedules',
        icon: <ScheduleIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.tests'),
        path: '/tests',
        icon: <QuizIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.reminders'),
        path: '/reminder-manage',
        icon: <NotificationsActiveIcon />,
        roles: ['CORPORATE_ADMIN'],
      },
    ],
  },
  {
    id: 'systemManagement',
    label: t('nav.systemManagement'),
    icon: <AdminPanelSettingsIcon />,
    roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
    children: [
      {
        label: t('nav.users'),
        path: '/users',
        icon: <PeopleIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.subscription'),
        path: '/subscription',
        icon: <CreditCardIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
      {
        label: t('nav.invoices'),
        path: '/invoices',
        icon: <ReceiptIcon />,
        roles: ['CORPORATE_ADMIN', 'SUPER_ADMIN'],
      },
    ],
  },
]

// スーパー管理者専用メニューカテゴリ
const getSuperAdminMenuCategories = (t: (key: string) => string): MenuCategory[] => [
  {
    id: 'superAdminManagement',
    label: t('nav.superAdminManagement'),
    icon: <SecurityIcon />,
    roles: ['SUPER_ADMIN'],
    children: [
      {
        label: t('nav.tenants'),
        path: '/tenant-management',
        icon: <BusinessIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.emailDelivery'),
        path: '/email-delivery',
        icon: <MarkEmailReadIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.emailTemplates'),
        path: '/email-templates',
        icon: <EmailIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.roleFeatures'),
        path: '/role-features',
        icon: <SecurityIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.billing'),
        path: '/billing',
        icon: <PaymentIcon />,
        roles: ['SUPER_ADMIN'],
      },
      {
        label: t('nav.tenantReminders'),
        path: '/tenant-reminder-manage',
        icon: <NotificationsActiveIcon />,
        roles: ['SUPER_ADMIN'],
      },
    ],
  },
]

export const BottomNavigation: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, canAccessRoute } = useAuth()
  const theme = useTheme()

  // ドロワーの開閉状態
  const [drawerOpen, setDrawerOpen] = useState(false)
  // 階層メニューの開閉状態
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({})

  // メインナビゲーションアイテムを取得
  const mainItems = getMainNavigationItems(t).filter((item) => {
    if (!item.roles) return true
    return user && item.roles.includes(user.role)
  })

  // 階層メニューカテゴリを取得（ユーザーのロールとロール機能設定でフィルタリング）
  const menuCategories = getMenuCategories(t)
    .filter((category) => {
      if (!category.roles) return true
      return user && category.roles.includes(user.role)
    })
    .map((category) => ({
      ...category,
      // 子アイテムをロール機能設定でフィルタリング
      children: category.children.filter((child) => {
        // まず静的なロールチェック
        if (child.roles && (!user || !child.roles.includes(user.role))) {
          return false
        }
        // ロール機能設定によるアクセス制御
        return canAccessRoute(child.path)
      }),
    }))
    // 子アイテムがないカテゴリは除外
    .filter((category) => category.children.length > 0)

  // スーパー管理者専用カテゴリ
  const superAdminCategories = getSuperAdminMenuCategories(t)
    .filter((category) => {
      if (!category.roles) return true
      return user && category.roles.includes(user.role)
    })
    .map((category) => ({
      ...category,
      // 子アイテムをロール機能設定でフィルタリング
      children: category.children.filter((child) => {
        // まず静的なロールチェック
        if (child.roles && (!user || !child.roles.includes(user.role))) {
          return false
        }
        // ロール機能設定によるアクセス制御
        return canAccessRoute(child.path)
      }),
    }))
    // 子アイテムがないカテゴリは除外
    .filter((category) => category.children.length > 0)

  // 現在のパスに基づいてアクティブなインデックスを取得
  const currentValue = mainItems.findIndex(
    (item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  )

  // カテゴリの開閉を切り替える
  const handleCategoryToggle = (categoryId: string) => {
    setOpenCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }))
  }

  const handleNavigation = (path: string, label: string) => {
    // テスト実施中のチェック
    const isTestPage = location.pathname.startsWith('/test/')
    if (isTestPage) {
      const confirmLeave = window.confirm(t('dialogs.testInProgressNavigate', { page: label }))

      if (!confirmLeave) {
        return
      }
    }

    navigate(path)
    setDrawerOpen(false)
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    // 最後のアイテム（メニューボタン）がクリックされた場合
    if (newValue === mainItems.length) {
      setDrawerOpen(true)
      return
    }

    if (mainItems[newValue]) {
      const targetItem = mainItems[newValue]
      handleNavigation(targetItem.path, targetItem.label)
    }
  }

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: { xs: 'block', md: 'none' }, // モバイルでのみ表示
        }}
        elevation={8}
      >
        <MuiBottomNavigation
          value={currentValue >= 0 ? currentValue : false}
          onChange={handleChange}
          sx={{
            backgroundColor: 'background.paper',
            '& .MuiBottomNavigationAction-root': {
              color: 'text.secondary',
              '&.Mui-selected': {
                color: theme.palette.primary.main,
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.625rem',
                lineHeight: 1.2,
                '&.Mui-selected': {
                  fontSize: '0.6875rem',
                },
              },
            },
          }}
        >
          {mainItems.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={item.icon}
              sx={{
                minWidth: 'auto',
                padding: { xs: '6px 0 10px' },
              }}
            />
          ))}
          {/* メニューボタン（もっと見る） */}
          <BottomNavigationAction
            label={t('nav.menu') || 'メニュー'}
            icon={<MenuIcon />}
            sx={{
              minWidth: 'auto',
              padding: { xs: '6px 0 10px' },
            }}
          />
        </MuiBottomNavigation>
      </Paper>

      {/* フルメニュードロワー */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            maxHeight: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('nav.menu') || 'メニュー'}
          </Typography>
        </Box>
        <Divider />
        <List sx={{ pb: 8 }}>
          {/* トップレベルアイテム（ダッシュボード、理解度テスト） */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavigation('/dashboard', t('nav.dashboard'))}
              selected={location.pathname === '/dashboard'}
            >
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary={t('nav.dashboard')} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavigation('/my-enrollments', t('nav.myEnrollments'))}
              selected={location.pathname === '/my-enrollments'}
            >
              <ListItemIcon>
                <AssignmentIcon />
              </ListItemIcon>
              <ListItemText primary={t('nav.myEnrollments')} />
            </ListItemButton>
          </ListItem>

          {/* 階層メニューカテゴリ */}
          {menuCategories.map((category) => (
            <React.Fragment key={category.id}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleCategoryToggle(category.id)}>
                  <ListItemIcon>{category.icon}</ListItemIcon>
                  <ListItemText primary={category.label} />
                  {openCategories[category.id] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openCategories[category.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {category.children.map((child) => (
                    <ListItemButton
                      key={child.path}
                      sx={{ pl: 4 }}
                      onClick={() => handleNavigation(child.path, child.label)}
                      selected={location.pathname === child.path}
                    >
                      <ListItemIcon>{child.icon}</ListItemIcon>
                      <ListItemText primary={child.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}

          {/* スーパー管理者専用カテゴリ */}
          {superAdminCategories.map((category) => (
            <React.Fragment key={category.id}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleCategoryToggle(category.id)}>
                  <ListItemIcon>{category.icon}</ListItemIcon>
                  <ListItemText primary={category.label} />
                  {openCategories[category.id] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openCategories[category.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {category.children.map((child) => (
                    <ListItemButton
                      key={child.path}
                      sx={{ pl: 4 }}
                      onClick={() => handleNavigation(child.path, child.label)}
                      selected={location.pathname === child.path}
                    >
                      <ListItemIcon>{child.icon}</ListItemIcon>
                      <ListItemText primary={child.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Drawer>
    </>
  )
}
